import React, { useMemo } from 'react';
import { debounce as debounceFn } from '@go-shared/lib/utils';

export interface BubbleDebounceChildProps<T> {
  debounce: (param: T) => void;
}

export interface BubbleDebouncePropsBase<T> {
  onTrigger: [((param: T) => void) | undefined, number];
}

export type BubbleDebounceProps<T> = {
  children: (props: BubbleDebounceChildProps<T>) => React.ReactNode;
} & BubbleDebouncePropsBase<T>;

/* A note about the onTrigger prop:

You must write the type of the param prop with the return type of the inner function of the
debounce function to get strong types. In other words, in the example below, notice how the
param of onTrigger is written as of type string. If you omit this, then typescript cannot
determine the type of the param properly. This does not mean you can give it anything other
than a string type. If you set it as number, for example, it warns that "Hello, world!" is
not a number.

<BubbleDebounce onTrigger={[(param: string) => {}, 500]}>
  {
    ({debounce}) => {
      <Button onClick={() => debounce(() => "Hello, world!")} />
    }
  }
</BubbleDebounce>
*/

export default function BubbleDebounce<T>(props: BubbleDebounceProps<T>) {
  const { onTrigger, children } = props;

  const debounce = useMemo(() => {
    const [fn, ms] = onTrigger;
    return fn
      ? debounceFn(fn, ms)
      : () => {
          console.log('[BubbleDebounce] calling onTrigger on an undefined function');
        };
  }, [onTrigger]);

  return <>{children({ debounce })}</>;
}
